import * as React from 'react';
import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete } from '@mui/material';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { TApiUserLocation } from '../../types/User';

interface ILocationSelectModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (locationId: number | null) => void;
  locations: TApiUserLocation[];
  currentLocationId: number | null;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    formControl: {
      marginBottom: theme.spacing(2),
      width: '100%',
    },
  })
);

const LocationSelectModal: React.FC<ILocationSelectModalProps> = ({
  open,
  onClose,
  onApply,
  locations,
  currentLocationId,
}): JSX.Element => {
  const classes = useStyles({});
  const { t } = useTranslation();

  const [selectedId, setSelectedId] = useState<number | null>(currentLocationId);

  useEffect(() => {
    if (open) {
      setSelectedId(currentLocationId);
    }
  }, [open, currentLocationId]);

  const handleApply = () => {
    onApply(selectedId);
    onClose();
  };

  const selectedOption = locations.find((l) => l.id === selectedId) ?? null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <h3>{t('chemicals.select_location')}</h3>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ paddingTop: '20px' }}>
        <Autocomplete
          className={classes.formControl}
          options={locations}
          getOptionLabel={(option) => option.title}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedOption}
          onChange={(_e, value) => {
            setSelectedId(value?.id ?? null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('chemicals.location')}
              variant="outlined"
            />
          )}
        />
      </DialogContent>
      <DialogActions style={{ padding: '8px 24px 16px' }}>
        <Button onClick={handleApply} color="primary" variant="contained">
          {t('chemicals.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationSelectModal;
